'use client';

import { CloseIcon, NotificationInfoIcon, UserIcon } from '@repo/icons';
import { Button } from '@repo/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@repo/ui/dialog';
import { useTranslations } from 'next-intl';
import React from 'react';

interface CompleteProfileDialogProps {
  open: boolean;
  handleOpen: (state: boolean) => void;
  handleCompleteProfile: () => void;
  handleOnSkipCompleteProfile: () => void;
}

/**
 * The CompleteProfileDialog component is a dialog that prompts the user to complete their full profile registration.
 */
const CompleteProfileDialog = ({
  open,
  handleOpen,
  handleCompleteProfile,
  handleOnSkipCompleteProfile,
}: CompleteProfileDialogProps) => {
  const t = useTranslations('complete-profile-dialog');

  const onSkipCompleteProfile = () => {
    handleOnSkipCompleteProfile();
    handleOpen(false);
  };

  const onCompleteProfile = () => {
    handleCompleteProfile();
    handleOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpen}
    >
      <DialogContent
        className="flex w-11/12 flex-col items-start justify-center gap-y-8 overflow-x-hidden p-4 pt-1 sm:w-[480px]"
        data-testid="complete-profile-dialog"
      >
        <DialogTitle className="sticky left-0 top-0 flex w-full flex-row items-center justify-between vt-font-highlight-font-family vt-text-size-big vt-headings-text sm:vt-text-size-big-sm">
          <span>{t('title')}</span>
          <DialogClose>
            <Button
              variant="blank"
              size="sm"
            >
              <CloseIcon />
            </Button>
          </DialogClose>
        </DialogTitle>
        <div className="flex w-full flex-col  items-center justify-center">
          <div className="mb-5 flex items-center justify-center gap-x-1 vt-font-highlight-font-family vt-text-size-medium vt-headings-text sm:vt-text-size-medium-sm">
            <NotificationInfoIcon />
            <span>{t('subtitle')}</span>
          </div>
          <div className="text-center vt-text-size-small sm:vt-text-size-small-sm">{t('description')}</div>
        </div>
        <div className="flex w-full justify-center gap-x-2">
          <Button
            variant="inverted"
            onClick={onSkipCompleteProfile}
            className="flex flex-row items-center justify-between vt-font-highlight-font-family vt-text-size-small sm:vt-text-size-small-sm"
          >
            {t('skip-button')}
          </Button>
          <Button
            onClick={onCompleteProfile}
            className=" flex flex-row items-center justify-between gap-x-2 vt-font-highlight-font-family vt-text-size-small sm:vt-text-size-small-sm"
          >
            <UserIcon className="size-5 shrink-0" />
            <span>{t('complete-profile-button')}</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteProfileDialog;
